import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import {
  Sparkles, ArrowRight, CheckCircle2, Building2, LayoutDashboard,
  ShieldCheck, UserCheck, Smartphone, BarChart3, Users,
  Mail, Phone, MapPin, Clock,
  // Real-feature icons
  CalendarCheck, Wallet, FileText, BookOpen, MessageSquareHeart,
  GraduationCap, ClipboardList, Globe,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const HERO_IMG = "";  // No default fallback — hero shows only when super-admin uploads/sets one in Settings

// ---------------------------------------------------------------------------
// Pricing section — pulled live from the SA-managed subscription plan catalog.
// Falls back to a graceful "Pricing coming soon" if the SA hasn't published any
// plans yet.
// ---------------------------------------------------------------------------
function PricingSection() {
  const [plans, setPlans] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(0); // carousel page (3 plans per page)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/subscription-plans");
        setPlans(r.data?.items || []);
      } catch { /* ignore */ }
      finally { setLoaded(true); }
    })();
  }, []);

  // Featured plan: use the dedicated `is_recommended` flag (single radio).
  // Falls back to the legacy badge regex so older plans still get the star.
  const popularIdx = plans.findIndex(
    (p) => p.is_recommended || /(popular|recommended|featured)/i.test(p.badge || "")
  );
  // Auto-compute yearly savings against the cheapest monthly paid plan.
  const cheapestMonthly = plans.filter((p) => !p.is_free && p.billing_cycle === "monthly")
    .reduce((min, p) => (min === null || p.price_php < min.price_php ? p : min), null);

  const savingsFor = (yearlyPlan) => {
    if (!cheapestMonthly || yearlyPlan.billing_cycle !== "yearly" || !yearlyPlan.price_php) return null;
    const fullYearMonthly = cheapestMonthly.price_php * 12;
    if (yearlyPlan.price_php >= fullYearMonthly) return null;
    const pct = Math.round(((fullYearMonthly - yearlyPlan.price_php) / fullYearMonthly) * 100);
    return pct >= 1 ? pct : null;
  };

  if (!loaded) return null;

  // Carousel: 3 plans per page on desktop; arrows let owner page through.
  const PER_PAGE = 3;
  const useCarousel = plans.length > PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(plans.length / PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = useCarousel
    ? plans.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE)
    : plans;
  const colsClass = {
    1: "md:grid-cols-1 max-w-md",
    2: "md:grid-cols-2 max-w-3xl",
    3: "md:grid-cols-3 max-w-6xl",
  }[Math.min(visible.length, 3)] || "md:grid-cols-3 max-w-6xl";

  return (
    <section
      id="pricing"
      className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 scroll-mt-20"
      data-testid="pricing-section"
    >
      <div className="text-center mb-12 max-w-2xl mx-auto">
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Pricing</h2>
        <p className="mt-4 text-lg text-slate-500">Choose the plan that fits your school's stage. Upgrade anytime.</p>
      </div>
      {plans.length === 0 ? (
        <p className="text-center text-slate-500">Pricing coming soon.</p>
      ) : (
        <div className="relative">
          {useCarousel && (
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              aria-label="Previous plans"
              data-testid="pricing-prev-btn"
              className="hidden md:flex absolute -left-3 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white border border-slate-200 shadow-floating hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
          )}

          <div className={`grid grid-cols-1 ${colsClass} gap-6 mx-auto items-stretch`}>
            {visible.map((p) => {
              const realIdx = plans.findIndex((x) => x.id === p.id);
              return (
                <PricingTile
                  key={p.id}
                  p={p}
                  i={realIdx}
                  popularIdx={popularIdx}
                  savingsFor={savingsFor}
                />
              );
            })}
          </div>

          {useCarousel && (
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              aria-label="Next plans"
              data-testid="pricing-next-btn"
              className="hidden md:flex absolute -right-3 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white border border-slate-200 shadow-floating hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          )}

          {useCarousel && (
            <div className="flex items-center justify-center gap-3 mt-6 md:hidden">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                aria-label="Previous plans"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white border border-slate-200 shadow disabled:opacity-40"
              ><ChevronLeft className="w-5 h-5" /></button>
              <span className="text-xs text-slate-500" data-testid="pricing-carousel-hint">
                Page {safePage + 1} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                aria-label="Next plans"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white border border-slate-200 shadow disabled:opacity-40"
              ><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}

          {useCarousel && (
            <div className="hidden md:flex items-center justify-center gap-2 mt-6">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setPage(idx)}
                  aria-label={`Go to page ${idx + 1}`}
                  className={`h-2 rounded-full transition-all ${idx === safePage ? "w-8 bg-[#2563EB]" : "w-2 bg-slate-300"}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PricingTile({ p, i, popularIdx, savingsFor }) {
  const featured = popularIdx === i;
  const yearlySave = savingsFor(p);
  return (
    <div
      className={`relative rounded-3xl p-7 border shadow-floating flex flex-col h-full ${
        featured
          ? "bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white border-transparent md:scale-105 ring-4 ring-[#2563EB]/20"
          : "bg-white border-slate-200/70"
      }`}
      data-testid={`pricing-plan-${p.id}`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-950 text-[10px] uppercase font-bold tracking-wider rounded-full px-3 py-1 shadow">
          ★ Recommended
        </div>
      )}
      {p.badge && !featured && (
        <span className="text-[10px] uppercase font-bold tracking-wider rounded-full px-2 py-0.5 inline-block mb-2 self-start bg-[#DBEAFE] text-[#1D4ED8]">
          {p.badge}
        </span>
      )}
      <h3 className={`font-heading text-xl font-bold ${featured ? "text-white" : ""}`}>{p.name}</h3>
      <p className={`mt-2 text-3xl font-heading font-bold ${featured ? "text-white" : "text-[#2563EB]"}`}>
        {p.is_free ? "Free" : <>₱{p.price_php.toLocaleString()}<span className={`text-sm font-normal ${featured ? "text-white/80" : "text-slate-500"}`}> / {p.billing_cycle}</span></>}
      </p>
      {yearlySave && (
        <span className={`mt-1 text-[11px] uppercase font-bold tracking-wider rounded-full px-2 py-0.5 inline-block self-start ${featured ? "bg-amber-300 text-amber-950" : "bg-emerald-50 text-emerald-700"}`} data-testid={`pricing-savings-${p.id}`}>
          Save {yearlySave}% vs monthly
        </span>
      )}
      {p.description && <p className={`mt-2 text-sm whitespace-pre-line ${featured ? "text-white/90" : "text-slate-500"}`}>{p.description}</p>}
      <ul className={`mt-3 space-y-1 text-xs ${featured ? "text-white/85" : "text-slate-600"}`}>
        <li>• {p.modules?.length || 0} modules included</li>
        {!p.is_free && <li>• {p.duration_days}-day cycle</li>}
      </ul>
      <div className="mt-auto pt-6">
        <Link to="/register">
          <Button className={`w-full rounded-full h-11 font-semibold ${featured ? "bg-white text-[#2563EB] hover:bg-slate-100" : "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"}`}>
            {p.is_free ? "Start free" : "Get started"}
          </Button>
        </Link>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// FEATURES — the 9 real modules SchoolBoss ships today (3×3 grid).
// Each card uses a unique pastel tint so the grid feels alive (not 9 identical
// blue tiles). Order: enrollment → daily ops → finance → comms → audit.
// ---------------------------------------------------------------------------
const FEATURES = [
  {
    icon: Building2,
    title: "Multi-Branch Management",
    desc: "Operate unlimited campuses under one owner account. Each branch has its own admin, students, finances and activity log.",
    tint: "bg-[#DBEAFE] text-[#2563EB]",
  },
  {
    icon: Users,
    title: "Student Records",
    desc: "Complete student profiles with photos, guardians, class & section, enrollment status and full address history.",
    tint: "bg-[#FFE4E6] text-[#BE123C]",
  },
  {
    icon: CalendarCheck,
    title: "Attendance Tracking",
    desc: "Mark daily attendance per class with present / absent / late / excused statuses, plus per-student history and summaries.",
    tint: "bg-[#DCFCE7] text-[#15803D]",
  },
  {
    icon: Wallet,
    title: "Tuition & Payments",
    desc: "Record payments, track balances and get traffic-light alerts for overdue tuition. Auto-numbered receipts included.",
    tint: "bg-[#FEF3C7] text-[#92400E]",
  },
  {
    icon: BarChart3,
    title: "Accounting & Cash Flow",
    desc: "Income, expenses, cash-on-hand, fund transfers and a consolidated Cash Analysis report per branch or all branches.",
    tint: "bg-[#FAE8FF] text-[#7E22CE]",
  },
  {
    icon: FileText,
    title: "Pay Slip Generation",
    desc: "Auto-build employee pay slips with overtime, deductions and adjustments. Save snapshots, reprint anytime.",
    tint: "bg-[#E0E7FF] text-[#3730A3]",
  },
  {
    icon: BookOpen,
    title: "Grades & Report Cards",
    desc: "Record assessment scores per subject and period, then auto-generate printable report cards for each child.",
    tint: "bg-[#FFE8D6] text-[#9A3412]",
  },
  {
    icon: MessageSquareHeart,
    title: "Progress Feedback",
    desc: "Teachers leave structured feedback (strengths, focus areas, behavior) that parents can see in their portal.",
    tint: "bg-[#FCE7F3] text-[#BE185D]",
  },
  {
    icon: GraduationCap,
    title: "Branded Parent Portal",
    desc: "Parents log in via your school's public URL and see only what you allow — attendance, grades, payments, feedback.",
    tint: "bg-[#CFFAFE] text-[#0E7490]",
  },
];

// ---------------------------------------------------------------------------
// "Also included" — secondary capabilities shown as a chip row under the grid.
// Keeps the marketing page honest about everything that ships today without
// inflating to 15 full feature cards.
// ---------------------------------------------------------------------------
const ALSO_INCLUDED = [
  { icon: ShieldCheck,    label: "Role-based access (Owner / Branch Admin / Special Access / Parent)" },
  { icon: Globe,          label: "Public school landing page with your own URL & theme" },
  { icon: ClipboardList,  label: "Activity audit log of every change" },
  { icon: Smartphone,     label: "Works on phones, tablets and desktops" },
  { icon: CheckCircle2,   label: "Auto-compressed photo uploads (logos, students, employees)" },
];

// ---------------------------------------------------------------------------
// HOW IT WORKS (5 numbered steps)
// ---------------------------------------------------------------------------
const STEPS = [
  { n: 1, title: "School owner registers", desc: "Create an account and submit your school details." },
  { n: 2, title: "Super Admin approves",   desc: "We review and approve your school registration." },
  { n: 3, title: "Owner adds branches",    desc: "Add as many branches as you operate." },
  { n: 4, title: "Super Admin approves branches", desc: "Branches are reviewed before going live." },
  { n: 5, title: "Branch admins log in",   desc: "Each admin manages their assigned branch only." },
];

// ---------------------------------------------------------------------------
// LANDING PAGE
// ---------------------------------------------------------------------------
export default function LandingPage() {
  const [site, setSite] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/site-content")
      .then(({ data }) => { if (alive) setSite(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Prefer the dedicated streaming endpoint (cacheable, parallel-loaded with
  // the rest of the page) — falls back to the external URL field, then to
  // the legacy inline base64 (only present if /site-content was called with
  // ?include_hero=true, which the marketing page never does).
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const heroVer = site?.updated_at ? `?v=${encodeURIComponent(site.updated_at)}` : "";
  const heroImg = site?.has_hero_image
    ? `${BACKEND_URL}/api/site-content/hero-image${heroVer}`
    : (site?.hero_image_url || site?.hero_image_base64 || HERO_IMG);
  const footer = site?.footer || {};
  return (
    <div className="marketing-font min-h-screen bg-[#F4F8FF] text-slate-900">
      <Navbar />

      {/* ------------------- HERO ------------------- */}
      <section
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-20"
        data-testid="hero-section"
      >
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-6 space-y-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200/80 px-3.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" /> Trusted by growing schools
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.04] tracking-tight">
              Smart School<br/>
              Management for{" "}
              <span className="text-[#2563EB]">Growing<br/>Schools</span>
            </h1>
            <p className="text-lg text-slate-500 max-w-xl leading-relaxed">
              Manage your school, branches, admins, students, staff, and reports in one secure and responsive platform.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/register" data-testid="hero-register-cta">
                <Button className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 h-14 text-base font-semibold shadow-floating">
                  Register Your School <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-login-cta">
                <Button
                  variant="outline"
                  className="rounded-full border-slate-200 bg-white text-slate-800 hover:bg-slate-50 px-8 h-14 text-base font-semibold shadow-sm"
                >
                  Login
                </Button>
              </Link>
            </div>
            {/* Trust badges */}
            <div className="flex flex-wrap gap-x-7 gap-y-2 pt-3 text-sm font-medium text-slate-600">
              {["Role-based access", "Approval workflows", "Mobile responsive"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#15803D]" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[5/4] rounded-[2rem] bg-gradient-to-br from-[#DBEAFE] via-[#EFF6FF] to-white shadow-floating overflow-hidden">
              {heroImg && (
                <img
                  src={heroImg}
                  alt="SchoolBoss dashboard preview"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  data-testid="hero-image"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------- FEATURES ------------------- */}
      <section
        id="features"
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 scroll-mt-20"
        data-testid="features-section"
      >
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Everything you need to run a modern school
          </h2>
          <p className="mt-4 text-lg text-slate-500">
            Nine built-in modules — enrollment, attendance, finance, comms and reporting — for schools, playschools, learning centers, tutorial centers and therapy centers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-3xl border border-slate-200/70 p-7 shadow-floating hover:-translate-y-1 hover:shadow-floating-hover transition-all duration-300"
              data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${f.tint}`}>
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-slate-500 leading-relaxed text-[15px]">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Also included — chip row */}
        <div className="mt-10 max-w-4xl mx-auto" data-testid="also-included">
          <p className="text-center text-xs font-heading font-bold uppercase tracking-wider text-slate-400 mb-4">
            Also included
          </p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {ALSO_INCLUDED.map((c) => (
              <span
                key={c.label}
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200/70 rounded-full px-3.5 py-1.5 text-sm text-slate-600 shadow-sm"
              >
                <c.icon className="w-4 h-4 text-[#2563EB]" /> {c.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------- HOW IT WORKS ------------------- */}
      <section
        id="how-it-works"
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 scroll-mt-20"
        data-testid="howitworks-section"
      >
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">How it works</h2>
          <p className="mt-4 text-lg text-slate-500">
            From registration to running multiple branches in five clear steps.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="bg-white rounded-3xl border border-slate-200/70 p-6 shadow-floating text-center hover:-translate-y-1 transition-transform"
              data-testid={`step-${s.n}`}
            >
              <div className="w-10 h-10 mx-auto rounded-full bg-[#DCFCE7] text-[#15803D] flex items-center justify-center font-heading font-bold text-lg mb-4">
                {s.n}
              </div>
              <div className="font-heading font-bold text-foreground">{s.title}</div>
              <div className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------- PRICING ------------------- */}
      <PricingSection />

      {/* ------------------- CTA BANNER ------------------- */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
        <div
          className="rounded-[2rem] bg-gradient-to-br from-[#2563EB] via-[#2563EB] to-[#1D4ED8] p-10 sm:p-14 text-white shadow-floating relative overflow-hidden"
          data-testid="cta-banner"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full pointer-events-none" />
          <div className="absolute -bottom-32 -left-10 w-96 h-96 bg-white/5 rounded-full pointer-events-none" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                Ready to bring your school online?
              </h2>
              <p className="mt-3 text-white/85 max-w-xl">
                Register your school today, get approved, and start onboarding branches in minutes. Pricing scales with your number of branches.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/register" data-testid="cta-register-btn">
                <Button className="rounded-full bg-white text-[#2563EB] hover:bg-slate-100 px-7 h-12 font-semibold shadow-sm">
                  Register Your School
                </Button>
              </Link>
              <Link to="/login" data-testid="cta-login-btn">
                <Button
                  variant="outline"
                  className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 px-7 h-12 font-semibold"
                >
                  <Users className="w-4 h-4 mr-1.5" /> Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------- FOOTER ------------------- */}
      <footer
        id="contact"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 scroll-mt-20"
        data-testid="landing-footer"
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="col-span-2 lg:col-span-1">
            <img src="/brand/logo-day.png" alt="SchoolBoss" className="h-11 w-auto select-none" draggable="false" />
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">
              {footer.tagline || "Smart, secure, multi-branch school management built for growing institutions."}
            </p>
          </div>
          <div>
            <h4 className="font-heading font-bold text-slate-900 mb-4">Product</h4>
            <ul className="space-y-3 text-sm text-slate-600">
              <li><a href="#features" className="hover:text-[#2563EB]">Features</a></li>
              <li><a href="#how-it-works" className="hover:text-[#2563EB]">How it works</a></li>
              <li><a href="#pricing" className="hover:text-[#2563EB]">Pricing</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold text-slate-900 mb-4">Account</h4>
            <ul className="space-y-3 text-sm text-slate-600">
              <li><Link to="/login" className="hover:text-[#2563EB]">Login</Link></li>
              <li><Link to="/register" className="hover:text-[#2563EB]">Register your school</Link></li>
              <li><Link to="/privacy" className="hover:text-[#2563EB]">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-[#2563EB]">Terms of Use</Link></li>
              <li><a href="#contact" className="hover:text-[#2563EB]">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold text-slate-900 mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-slate-600">
              {footer.email && <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" /> {footer.email}</li>}
              {footer.phone && <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" /> {footer.phone}</li>}
              {footer.address && <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" /> {footer.address}</li>}
              {footer.hours && <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" /> {footer.hours}</li>}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200/70 mt-10 pt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} {footer.company_name || "SchoolBoss.Ph"} — All rights reserved.</span>
          <span>Built for schools, playschools, learning centers and therapy centers.</span>
        </div>
      </footer>
    </div>
  );
}
